import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';

interface NavigationProps {
  logoSrc: string;
}

// Styled components for the navigation
const NavContainer = styled.nav<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: ${theme.colors.white};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: transform 0.3s ease-in-out;
  transform: translateY(${({ visible }) => (visible ? '0' : '-100%')});
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[2]} ${theme.spacing[3]};
  }
`;

const NavContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled.img`
  height: 40px;
  width: auto;
  
  @media (max-width: ${theme.breakpoints.md}) {
    height: 32px;
  }
`;

const NavLinks = styled.ul`
  display: flex;
  list-style: none;
  gap: ${theme.spacing[6]};
  margin: 0;
  padding: 0;
  
  @media (max-width: ${theme.breakpoints.md}) {
    gap: ${theme.spacing[3]};
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    display: none; // Hide on small screens
  }
`;

const NavLink = styled.a`
  color: ${theme.colors.gray[700]};
  text-decoration: none;
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  transition: color 0.2s ease;
  
  &:hover {
    color: ${theme.colors.primary};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  color: ${theme.colors.gray[700]};
  cursor: pointer;
  padding: ${theme.spacing[1]};
  width: 40px;
  border-radius: ${theme.borderRadius.xl};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    display: block;
  }
`;

const MobileMenu = styled.div<{ open: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${theme.colors.white};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: ${theme.spacing[4]};
  border-radius: 0 0 ${theme.borderRadius.lg} ${theme.borderRadius.lg};
  transform-origin: top;
  transform: scaleY(${({ open }) => (open ? 1 : 0)});
  opacity: ${({ open }) => (open ? 1 : 0)};
  transition: transform 0.2s ease, opacity 0.2s ease;
  display: none;
  z-index: 999;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    display: block;
  }
`;

const MobileMenuLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const MobileMenuLink = styled.a`
  color: ${theme.colors.gray[700]};
  text-decoration: none;
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.medium};
  padding: ${theme.spacing[2]} 0;
  display: block;
  transition: color 0.2s ease;
  border-bottom: 1px solid ${theme.colors.gray[200]};
  
  &:hover {
    color: ${theme.colors.primary};
  }
`;

const Navigation: React.FC<NavigationProps> = ({ logoSrc }) => {
  const [visible, setVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Get the hero section height - we'll show the nav once scrolled past it
      const heroSection = document.getElementById('hero');
      const heroHeight = heroSection?.offsetHeight || 100;
      
      // Check if we've scrolled past the hero section
      if (window.scrollY > heroHeight - 100) {
        setVisible(true);
      } else {
        setVisible(false);
        // Close mobile menu when nav is hidden
        setMobileMenuOpen(false);
      }
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    
    // Call once to set initial state
    handleScroll();
    
    // Handle clicks outside the mobile menu to close it
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (mobileMenuOpen && !target.closest('.mobile-menu-container')) {
        setMobileMenuOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    
    // Remove event listeners on cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the click from triggering the outside click handler
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    // Close the mobile menu when a link is clicked
    setMobileMenuOpen(false);
    
    // Get the target element
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      // Get the navigation height to offset the scroll position
      const navHeight = document.querySelector('nav')?.offsetHeight || 0;
      
      // Scroll to the element with a smooth behavior, accounting for the nav height
      window.scrollTo({
        top: targetElement.offsetTop - navHeight,
        behavior: 'smooth'
      });
    }
  };
  
  const handleLogoClick = (e: React.MouseEvent<HTMLImageElement>) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    
    const heroSection = document.getElementById('hero');
    if (heroSection) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  return (
    <NavContainer visible={visible}>
      <NavContent className="mobile-menu-container">
        <LogoContainer>
          <Logo src={logoSrc} alt="Green Growers Club Logo" onClick={handleLogoClick} style={{cursor: 'pointer'}} />
        </LogoContainer>
        
        <NavLinks>
          <li><NavLink href="#about" onClick={(e) => handleLinkClick(e, 'about')}>About</NavLink></li>
          <li><NavLink href="#how-it-works" onClick={(e) => handleLinkClick(e, 'how-it-works')}>How It Works</NavLink></li>
          <li><NavLink href="#independence" onClick={(e) => handleLinkClick(e, 'independence')}>Transparency</NavLink></li>
          <li><NavLink href="#join" onClick={(e) => handleLinkClick(e, 'join')}>Connect</NavLink></li>
        </NavLinks>
        
        <MobileMenuButton onClick={toggleMobileMenu}>
          {mobileMenuOpen ? '✕' : '☰'}
        </MobileMenuButton>
        
        <MobileMenu open={mobileMenuOpen}>
          <MobileMenuLinks>
            <li><MobileMenuLink href="#about" onClick={(e) => handleLinkClick(e, 'about')}>About</MobileMenuLink></li>
            <li><MobileMenuLink href="#how-it-works" onClick={(e) => handleLinkClick(e, 'how-it-works')}>How It Works</MobileMenuLink></li>
            <li><MobileMenuLink href="#features" onClick={(e) => handleLinkClick(e, 'features')}>Features</MobileMenuLink></li>
            <li><MobileMenuLink href="#independence" onClick={(e) => handleLinkClick(e, 'independence')}>Transparency</MobileMenuLink></li>
            <li><MobileMenuLink href="#join" onClick={(e) => handleLinkClick(e, 'join')}>Connect</MobileMenuLink></li>
          </MobileMenuLinks>
        </MobileMenu>
      </NavContent>
    </NavContainer>
  );
};

export default Navigation; 